/** Default per-field combine operator in the filter dialog (all persona pages). */
export const DEFAULT_FILTER_OPERATOR = 'or';

export const FILTER_CATEGORIES = {
  demographics: 'Demographics',
  attributes: 'Domain Attributes',
  search: 'Search',
  interests: 'Interests',
  devices: 'Devices',
};

export const INCOME_LEVELS = [
  'low',
  'medium-low',
  'medium',
  'medium-high',
  'high',
];

export const FIXED_DEMOGRAPHIC_KEYS = new Set([
  'age',
  'gender',
  'occupation',
  'civil_status',
  'qualification',
  'income',
  'country',
  'geographic_area_of_residence',
]);

const KEY_ALIASES = {
  residence: 'geographic_area_of_residence',
  civilstatus: 'civil_status',
  marital_status: 'civil_status',
  job_title: 'occupation',
  education_level: 'qualification',
  annual_income: 'income',
  area: 'geographic_area_of_residence',
  // Telco schema declares this attribute as `segments`; persona data stores
  // it as `segmentation`. Fold to one canonical key so the schema field and
  // the data-discovered field resolve to a single Segmentation filter.
  segments: 'segmentation',
};

export function normalizeFieldKey(key) {
  const normalized = String(key || '')
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/\s+/g, '_');

  return KEY_ALIASES[normalized] || normalized;
}

/** Detect two schema keys that collapse to the same filter/persona key (e.g. income + annual_income). */
export function findAliasedAttributeKeyConflict(keys = []) {
  const seen = new Map();
  for (const key of keys) {
    const raw = String(key || '').trim();
    if (!raw) continue;
    const normalized = normalizeFieldKey(raw);
    if (seen.has(normalized)) {
      return { normalized, keys: [seen.get(normalized), raw] };
    }
    seen.set(normalized, raw);
  }
  return null;
}

export function isFixedDemographicKey(key) {
  return FIXED_DEMOGRAPHIC_KEYS.has(normalizeFieldKey(key));
}

// Fixed demographic keys whose filter CONTROL is schema-driven: their
// control type was only ever hard-coded, and the schema's declared type
// must win instead. Clarified requirement: the schema-driven type-aware
// behaviour applies to EVERY attribute, with no exceptions — including the
// long-standing fixed/system ones (age, gender, income, country, ...). So
// this now covers the full FIXED_DEMOGRAPHIC_KEYS set: a `number` attribute
// (e.g. age, income) becomes a range slider using the schema min/max, a
// `single_select` attribute (e.g. gender, civil_status) becomes a
// multiselect whose options are the schema's own configured choices — the
// same list shown in the Persona Framework Editor, not arbitrary persona
// data — and `text_short`/anything else (e.g.
// occupation, country) becomes free text. FIXED_DEMOGRAPHIC_FIELDS below is
// kept as the fallback control for any of these keys a domain's schema does
// not (yet) declare — see isSchemaBuildableAttributeKey / getAllFilterFields.
export const SCHEMA_DRIVEN_FIXED_KEYS = new Set(FIXED_DEMOGRAPHIC_KEYS);

// A schema attribute contributes its own (type-aware) filter field when it
// is either not a fixed demographic key at all, or one of the explicitly
// schema-driven fixed keys above.
export function isSchemaBuildableAttributeKey(key) {
  const normalized = normalizeFieldKey(key);
  return !FIXED_DEMOGRAPHIC_KEYS.has(normalized) || SCHEMA_DRIVEN_FIXED_KEYS.has(normalized);
}

// NOTE ON LABELS: static field labels below are i18n KEYS (e.g.
// 'personaFields.occupation'), not display text — components must call
// t(field.label) to render them. Custom/dynamic attribute fields (see
// buildCustomAttributeField below) use a plain formatted string instead of
// a dotted key, so t() safely returns it unchanged (no matching key exists)
// — this is what keeps custom attributes untranslated/dynamic on purpose.
export const FIXED_DEMOGRAPHIC_FIELDS = [
  {
    key: 'age',
    label: 'personamanagement.age',
    category: 'demographics',
    type: 'range',
    min: 0,
    max: 120,
    unit: 'years',
    defaultOperator: DEFAULT_FILTER_OPERATOR,
  },
  {
    key: 'gender',
    label: 'personaFields.gender',
    category: 'demographics',
    type: 'toggle',
    // Options are filled at runtime from schema choices or distinct persona values (B3).
    options: [],
    defaultOperator: DEFAULT_FILTER_OPERATOR,
  },
  {
    key: 'occupation',
    label: 'personaFields.occupation',
    category: 'demographics',
    type: 'multiselect',
    defaultOperator: DEFAULT_FILTER_OPERATOR,
  },
  {
    key: 'civil_status',
    label: 'personaFields.civil_status',
    category: 'demographics',
    // No hard-coded option list: the selectable values are derived from the
    // values actually present in the current persona data (see
    // FilterSettingsDialog.optionLists -> collectDistinctOptions), so
    // domain-specific values such as "Married, two adult children" appear.
    type: 'toggle',
    // Options are filled at runtime from schema choices or distinct persona values (B3).
    options: [],
    defaultOperator: DEFAULT_FILTER_OPERATOR,
  },
  {
    key: 'qualification',
    label: 'personaFields.qualification',
    category: 'demographics',
    type: 'multiselect',
    defaultOperator: DEFAULT_FILTER_OPERATOR,
  },
  {
    key: 'income',
    label: 'personaFields.income',
    category: 'demographics',
    type: 'income_range',
    levels: INCOME_LEVELS,
    defaultOperator: DEFAULT_FILTER_OPERATOR,
  },
  {
    key: 'country',
    label: 'personaFields.country',
    category: 'demographics',
    type: 'country_select',
    defaultOperator: DEFAULT_FILTER_OPERATOR,
  },
  {
    key: 'geographic_area_of_residence',
    label: 'filtersettings.geographicArea',
    category: 'demographics',
    type: 'tags',
    defaultOperator: DEFAULT_FILTER_OPERATOR,
  },
];

export const SEARCH_FIELDS = [
  {
    key: 'name_search',
    label: 'personamanagement.name',
    category: 'search',
    type: 'text',
    defaultOperator: DEFAULT_FILTER_OPERATOR,
  },
  {
    key: 'persona_tags',
    label: 'filtersettings.tags',
    category: 'search',
    type: 'tags',
    defaultOperator: DEFAULT_FILTER_OPERATOR,
  },
];

export const INTEREST_DEVICE_FIELDS = [
  {
    key: 'personal_interests',
    label: 'personaedit.fields.interests',
    category: 'interests',
    type: 'tags',
    defaultOperator: DEFAULT_FILTER_OPERATOR,
  },
  {
    key: 'devices',
    label: 'filtersettings.devices',
    category: 'devices',
    type: 'multiselect',
    defaultOperator: DEFAULT_FILTER_OPERATOR,
  },
];

// Known predefined option values -> i18n keys. Only covers the fixed
// toggle/income options above; any value not listed here (custom attribute
// values, free-text data, etc.) is returned unchanged by
// getFilterOptionLabel, so dynamic/custom attributes are never forced
// through translation.
export const FILTER_OPTION_I18N_KEYS = {
  female: 'dashboard.female',
  male: 'dashboard.male',
  other: 'filtersettings.genderOther',
  single: 'filtersettings.civilStatusSingle',
  married: 'filtersettings.civilStatusMarried',
  divorced: 'filtersettings.civilStatusDivorced',
  low: 'dashboard.low2',
  'medium-low': 'dashboard.mediumLow',
  medium: 'dashboard.medium',
  'medium-high': 'dashboard.mediumHigh',
  high: 'dashboard.high2',
};

export function getFilterOptionLabel(t, value) {
  const key = FILTER_OPTION_I18N_KEYS[value];
  return key && typeof t === 'function' ? t(key) : value;
}

export const STATIC_TOP_LEVEL_ATTRIBUTE_KEYS = new Set([
  'archetype',
  'segment',
]);

export const PERSONA_METADATA_KEYS = new Set([
  'id',
  'persona_id',
  'title',
  'name',
  'description',
  'image',
  'icons',
  'created',
  'created_at',
  'updated_at',
  'country',
  'tags',
  'personal_interests',
  'personalInterests',
  'personal_characteristics',
  'personalCharacteristics',
  'devices',
  'currentTechnologyAndTools',
  'data',
  'domain',
  'behaviourTemplate',
  'big_five_profile',
  'bigFiveProfile',
  'online_behavior_and_use_of_services',
  'online_behavior_and_automotive_engagement',
  'automotive_behavior_and_usage',
  'connection_type_and_usage',
  'personality',
  'main_needs',
  'workPatternsAndProfessionalUsage',
  'workExperience',
  'impressiveEpisode',
  'communicationStyle',
  'externalEvaluation',
  'workMotto',
  'recentConcerns',
  'strengthsAtWork',
  'weaknessesAtWork',
  'favoriteFoods',
  'dislikedFoods',
  'weekendRoutine',
]);

export const GENERATED_MAPPED_KEYS = new Set([
  'first_name',
  'last_name',
  'age',
  'gender',
  'job_title',
  'occupation',
  'marital_status',
  'civil_status',
  'area',
  'geographic_area_of_residence',
  'education_level',
  'qualification',
  'annual_income',
  'income',
  'country',
  'personal_interests',
  'interests',
  'devices',
]);

export function formatFieldLabel(key) {
  return normalizeFieldKey(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

// segments/segmentation is a known, near-universal attribute (seeded on
// every new domain, and Telco's legacy field) rather than a genuinely
// custom one — it gets the existing predefined translation key instead of
// the plain formatted string every other discovered attribute uses.
// Used by buildSchemaAttributeField below (buildCustomAttributeField
// resolves labels via resolveSchemaAttributeByKey instead, since a schema
// attribute already carries its own admin-configured `label`).
const KNOWN_PREDEFINED_ATTRIBUTE_LABELS = {
  segments: 'personaFields.segmentation',
  segmentation: 'personaFields.segmentation',
};

export function resolveSchemaAttributeByKey(key, schemaAttributes = []) {
  if (!key || !Array.isArray(schemaAttributes) || !schemaAttributes.length) {
    return null;
  }

  const normalized = normalizeFieldKey(key);
  return schemaAttributes.find((attr) => {
    if (!attr?.key) return false;
    if (normalizeFieldKey(attr.key) === normalized) return true;
    return (attr.previous_keys || []).some(
      (previousKey) => normalizeFieldKey(previousKey) === normalized,
    );
  }) || null;
}

export function buildCustomAttributeField(key, schemaAttributes = null) {
  const schemaAttr = resolveSchemaAttributeByKey(key, schemaAttributes);
  const label = schemaAttr?.label || formatFieldLabel(key);
  const base = { key, label, category: 'attributes', defaultOperator: DEFAULT_FILTER_OPERATOR, isCustom: true };

  // Callers that already know the key is schema-declared (getAllFilterFields,
  // via buildAttributeFieldsFromSchema) build a fully type-aware field and
  // never reach this branch — that path also derives numeric bounds from
  // persona data. This is the fallback for a key that isn't a fixed
  // demographic key and isn't (yet) known to be schema-buildable at the call
  // site, so it still resolves schemaAttr.type here rather than assuming
  // multiselect — a schema `number`/`text_short` custom attribute must not
  // silently render as a value-list picker.
  if (schemaAttr?.type === 'number') {
    const options = schemaAttr.options || {};
    const min = toFiniteNumber(options.min ?? options.min_value);
    const max = toFiniteNumber(options.max ?? options.max_value);
    const unit = typeof options.unit === 'string' ? options.unit.trim() : '';
    return {
      ...base,
      type: 'range',
      // Generic fallback bounds (mirrors the backend's own generic-integer
      // default) when the schema itself supplies neither bound — this path
      // has no persona list to derive one from, unlike resolveNumberFieldBounds.
      min: min ?? 0,
      max: max ?? 999,
      ...(unit ? { unit } : {}),
      defaultOperator: DEFAULT_FILTER_OPERATOR,
    };
  }

  if (schemaAttr?.type === 'single_select') {
    const choices = Array.isArray(schemaAttr.options?.choices)
      ? schemaAttr.options.choices.filter((choice) => choice != null && String(choice).trim() !== '')
      : [];
    return {
      ...base,
      type: 'multiselect',
      ...(choices.length ? { options: choices } : {}),
    };
  }

  if (schemaAttr?.type === 'text_short') {
    return { ...base, type: 'text', defaultOperator: DEFAULT_FILTER_OPERATOR };
  }

  // No schema entry at all (pure data-discovered legacy key) — the only
  // case where a value-list multiselect is still the right generic default.
  return { ...base, type: 'multiselect' };
}

// Coerce a schema-provided bound to a finite number, or undefined if it
// isn't one (missing, blank string, non-numeric) — never NaN/garbage.
function toFiniteNumber(value) {
  if (value === undefined || value === null || value === '') return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
}

function normalizeIncomeChoice(choice) {
  return String(choice ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');
}

/** True when single_select choices are narrative income bands (low … high). */
export function isIncomeLevelChoices(choices) {
  if (!Array.isArray(choices) || !choices.length) return false;
  return choices.every((choice) => {
    const normalized = normalizeIncomeChoice(choice);
    return INCOME_LEVELS.some(
      (level) => normalized === level || normalized.includes(level) || level.includes(normalized),
    );
  });
}

// Schema-driven counterpart to buildCustomAttributeField(): builds a filter
// field from a Persona Schema attribute (GET .../persona-schema), so the
// field's type/options come from the schema's own `type` rather than always
// defaulting to a value-list multiselect. Bounds for `number` attributes are
// read here as-is (possibly undefined) — resolveNumberFieldBounds() in
// personaFilter.js is responsible for filling in any bound the schema
// doesn't provide from persona data, since that step needs the persona
// list this module intentionally has no knowledge of.
export function buildSchemaAttributeField(attr) {
  const key = normalizeFieldKey(attr.key);
  const knownLabel = KNOWN_PREDEFINED_ATTRIBUTE_LABELS[key];
  const label = knownLabel || attr.label || formatFieldLabel(key);
  const base = { key, label, category: 'attributes', isCustom: true };

  if (attr.type === 'number') {
    const options = attr.options || {};
    const unit = typeof options.unit === 'string' ? options.unit.trim() : '';
    return {
      ...base,
      type: 'range',
      // min_value/max_value: legacy field names seen on older attribute
      // records (see PersonaFrameworkEditor.vue's own min ?? min_value).
      min: toFiniteNumber(options.min ?? options.min_value),
      max: toFiniteNumber(options.max ?? options.max_value),
      ...(unit ? { unit } : {}),
      defaultOperator: DEFAULT_FILTER_OPERATOR,
    };
  }

  if (attr.type === 'single_select') {
    // Predefined-list attribute -> the existing multi-value selector. The
    // Persona Framework Editor is the source of truth for a single_select
    // attribute's allowed values, so the displayed options are the schema's
    // own configured choices (attr.options.choices) — never an arbitrary
    // list of whatever free-form text happens to exist in current persona
    // data. FilterSettingsDialog.optionLists only falls back to data-driven
    // discovery when a field has no configured choices at all (a custom
    // attribute the schema doesn't declare, or a fixed key with no schema
    // entry for this domain).
    const choices = Array.isArray(attr.options?.choices)
      ? attr.options.choices.filter((choice) => choice != null && String(choice).trim() !== '')
      : [];
    if (key === 'income' && isIncomeLevelChoices(choices)) {
      return {
        ...base,
        category: 'demographics',
        type: 'income_range',
        levels: INCOME_LEVELS,
        defaultOperator: DEFAULT_FILTER_OPERATOR,
      };
    }
    return {
      ...base,
      type: 'multiselect',
      ...(choices.length ? { options: choices } : {}),
      defaultOperator: DEFAULT_FILTER_OPERATOR,
    };
  }

  // 'text_short' (and any unrecognized/future type) -> free-text search,
  // same as the existing name_search field.
  return {
    ...base,
    type: 'text',
    defaultOperator: DEFAULT_FILTER_OPERATOR,
  };
}

export function getStaticFilterFields() {
  return [
    ...FIXED_DEMOGRAPHIC_FIELDS,
    ...SEARCH_FIELDS,
    ...INTEREST_DEVICE_FIELDS,
  ];
}

export function getFilterFieldsByCategory(fields, category) {
  return fields.filter((field) => field.category === category);
}

export function getFilterField(key, allFields = getStaticFilterFields()) {
  return allFields.find((field) => field.key === key);
}

/**
 * Display label for a numeric range filter.
 * Age (unit years / key age) keeps the localized "years/歳" string;
 * other number attributes use their schema unit (e.g. cm) or bare min–max.
 */
export function formatNumberRangeLabel(min, max, { unit = '', fieldKey = '', t = (key, params) => key } = {}) {
  const resolvedUnit = String(unit || '').trim();
  const isAgeYears = resolvedUnit.toLowerCase() === 'years'
    || (!resolvedUnit && fieldKey === 'age');

  if (isAgeYears) {
    const localized = t('filtersettings.ageRangeValue', { min, max });
    if (localized && localized !== 'filtersettings.ageRangeValue') {
      return localized;
    }
    return `${min} – ${max} years`;
  }

  if (resolvedUnit) {
    const withUnit = t('filtersettings.numberRangeValueWithUnit', {
      min,
      max,
      unit: resolvedUnit,
    });
    if (withUnit && withUnit !== 'filtersettings.numberRangeValueWithUnit') {
      return withUnit;
    }
    return `${min} – ${max} ${resolvedUnit}`;
  }

  const bare = t('filtersettings.numberRangeValue', { min, max });
  if (bare && bare !== 'filtersettings.numberRangeValue') {
    return bare;
  }
  return `${min} – ${max}`;
}

export function mergeFilterFields(dynamicFields = []) {
  const staticKeys = new Set(getStaticFilterFields().map((field) => field.key));
  const seenDynamic = new Set();
  const uniqueDynamic = dynamicFields.filter((field) => {
    if (!field?.key || staticKeys.has(field.key) || seenDynamic.has(field.key)) {
      return false;
    }
    seenDynamic.add(field.key);
    return true;
  });
  return [...getStaticFilterFields(), ...uniqueDynamic];
}
